/**
 * SLOPage — manage Service Level Objectives with error budget gauges.
 */

import { useEffect, useState } from "react";
import { ErrorBudgetGauge } from "../../components/atoms/ErrorBudgetGauge";
import { apiFetch } from "../../lib/config";

interface SLOItem {
  id: string;
  name: string;
  service: string;
  service_name?: string;
  target_percent: string;
  window: "rolling_30" | "calendar_month";
  is_active: boolean;
}

interface ErrorBudget {
  id: number;
  period_start: string;
  period_end: string;
  total_seconds: number;
  downtime_seconds: number;
  uptime_percent: string;
  budget_consumed_percent: string;
  budget_remaining_minutes: number;
}

interface ServiceOption {
  id: string;
  name: string;
}

const WINDOW_LABELS: Record<string, string> = {
  rolling_30: "Rolling 30 days",
  calendar_month: "Calendar month",
};

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function budgetFillClass(consumed: number): string {
  if (consumed >= 90) return "ac-progress__fill--danger";
  if (consumed >= 70) return "ac-progress__fill--warn";
  return "";
}

export function SLOPage() {
  const [slos, setSlos] = useState<SLOItem[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [budgets, setBudgets] = useState<Record<string, ErrorBudget>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [formName, setFormName] = useState("");
  const [formService, setFormService] = useState("");
  const [formTarget, setFormTarget] = useState("99.9");
  const [formWindow, setFormWindow] = useState<"rolling_30" | "calendar_month">("rolling_30");
  const [formError, setFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Detail panel state
  const [selectedSlo, setSelectedSlo] = useState<SLOItem | null>(null);
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [downloading, setDownloading] = useState(false);

  // Confirm delete
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadSlos = async () => {
    try {
      setLoading(true);
      const data = await fetchJSON<SLOItem[]>("/api/v1/slo/");
      setSlos(data);
      const budgetMap: Record<string, ErrorBudget> = {};
      await Promise.all(
        data.map(async (slo) => {
          try {
            const b = await fetchJSON<ErrorBudget>(`/api/v1/slo/${slo.id}/budget/`);
            budgetMap[slo.id] = b;
          } catch {
            // No budget yet — ok
          }
        })
      );
      setBudgets(budgetMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load SLOs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSlos();
    fetchJSON<ServiceOption[]>("/api/v1/services/").then(setServices).catch(() => {});
    const to = new Date().toISOString().split("T")[0];
    const from = new Date(Date.now() - 30 * 86400 * 1000).toISOString().split("T")[0];
    setReportFrom(from);
    setReportTo(to);
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const targetNum = parseFloat(formTarget);
    if (isNaN(targetNum) || targetNum <= 0 || targetNum > 100) {
      setFormError("Target must be between 0 and 100.");
      return;
    }
    if (!formService) {
      setFormError("Please select a service.");
      return;
    }
    setCreating(true);
    try {
      await fetchJSON("/api/v1/slo/", {
        method: "POST",
        body: JSON.stringify({
          name: formName,
          service: formService,
          target_percent: targetNum.toFixed(4),
          window: formWindow,
        }),
      });
      setShowCreate(false);
      setFormName(""); setFormService(""); setFormTarget("99.9"); setFormWindow("rolling_30");
      await loadSlos();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiFetch(`/api/v1/slo/${id}/`, { method: "DELETE" });
      setDeletingId(null);
      if (selectedSlo?.id === id) setSelectedSlo(null);
      await loadSlos();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  };

  const handleDownloadReport = async () => {
    if (!selectedSlo) return;
    setDownloading(true);
    try {
      const url = `/api/v1/slo/${selectedSlo.id}/report/?output_format=csv&from=${reportFrom}&to=${reportTo}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`${res.status}`);
      const blob = await res.blob();
      downloadBlob(blob, `sla-report-${selectedSlo.id}.csv`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">SLO Management</h1>
          <p className="ac-page-subtitle">Define and monitor Service Level Objectives with error budget tracking</p>
        </div>
        <div className="ac-page-header__actions">
          <button className="aptean-btn aptean-btn--primary" onClick={() => setShowCreate(true)}>
            + Create SLO
          </button>
        </div>
      </div>

      {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {loading ? (
        <div style={{ padding: "3rem", textAlign: "center", color: "var(--aptean-text-secondary)" }}>Loading SLOs...</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th style={{ width: "28%" }}>SLO Name</th>
                <th>Target</th>
                <th>Window</th>
                <th>Uptime</th>
                <th style={{ width: "160px" }}>Error Budget</th>
                <th className="ac-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {slos.length === 0 ? (
                <tr><td colSpan={6}><div className="ac-empty"><div className="ac-empty__title">No SLOs defined</div><div className="ac-empty__body">Create one to start tracking service reliability.</div></div></td></tr>
              ) : slos.map((slo) => {
                const budget = budgets[slo.id];
                const consumed = budget ? parseFloat(budget.budget_consumed_percent) : 0;
                const remaining = budget ? budget.budget_remaining_minutes : 0;
                return (
                  <tr key={slo.id} style={selectedSlo?.id === slo.id ? { background: "var(--aptean-accent-subtle)" } : {}}>
                    <td>
                      <div className="ac-row-title">{slo.name}</div>
                      {slo.service_name && <div className="ac-row-sub">{slo.service_name}</div>}
                    </td>
                    <td className="ac-td--muted">{slo.target_percent}%</td>
                    <td className="ac-td--muted">{WINDOW_LABELS[slo.window] ?? slo.window}</td>
                    <td className="ac-td--muted">{budget ? `${budget.uptime_percent}%` : "—"}</td>
                    <td>
                      {budget ? (
                        <div>
                          <div className="ac-progress" style={{ marginBottom: "0.25rem" }}>
                            <div className={`ac-progress__fill ${budgetFillClass(consumed)}`} style={{ width: `${Math.min(consumed, 100)}%` }} />
                          </div>
                          <div style={{ fontSize: "0.75rem", color: "var(--aptean-text-secondary)" }}>
                            {consumed.toFixed(1)}% used &mdash; {remaining}m left
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <ErrorBudgetGauge
                            consumedPercent={consumed}
                            remainingMinutes={remaining}
                            targetPercent={parseFloat(slo.target_percent)}
                            size={48}
                          />
                          <span style={{ fontSize: "0.75rem", color: "var(--aptean-text-muted)" }}>No data</span>
                        </div>
                      )}
                    </td>
                    <td className="ac-td--right ac-td--actions">
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => setSelectedSlo(selectedSlo?.id === slo.id ? null : slo)}>
                          {selectedSlo?.id === slo.id ? "Close" : "Report"}
                        </button>
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem", color: "#a4262c", borderColor: "#f1a0a3" }} onClick={() => setDeletingId(slo.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedSlo && (
        <div className="ac-table-wrap" style={{ marginTop: "1.25rem", padding: "1.25rem" }}>
          <div style={{ fontWeight: 700, color: "var(--aptean-text-primary)", marginBottom: "0.75rem" }}>{selectedSlo.name} — Report Download</div>
          <p style={{ color: "var(--aptean-text-secondary)", fontSize: "0.8125rem", marginBottom: "1rem" }}>
            Download a CSV report of error budget history for this SLO.
          </p>
          <div style={{ display: "flex", gap: "0.875rem", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div className="ac-field">
              <label>From</label>
              <input type="date" className="aptean-input" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
            </div>
            <div className="ac-field">
              <label>To</label>
              <input type="date" className="aptean-input" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
            </div>
            <button className="aptean-btn aptean-btn--primary" onClick={handleDownloadReport} disabled={downloading}>
              {downloading ? "Downloading..." : "Download CSV"}
            </button>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Create SLO</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowCreate(false)}>&#10005;</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="ac-dialog__body">
                {formError && <div className="ac-alert ac-alert--error">{formError}</div>}
                <div className="ac-field">
                  <label>Name *</label>
                  <input className="aptean-input" value={formName} onChange={(e) => setFormName(e.target.value)} required placeholder="e.g. ERP Availability" />
                </div>
                <div className="ac-field">
                  <label>Service *</label>
                  <select className="aptean-input" value={formService} onChange={(e) => setFormService(e.target.value)} required>
                    <option value="">-- select service --</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="ac-field-row">
                  <div className="ac-field">
                    <label>Target %</label>
                    <input className="aptean-input" type="number" min="0" max="100" step="0.001" value={formTarget} onChange={(e) => setFormTarget(e.target.value)} required />
                  </div>
                  <div className="ac-field">
                    <label>Window</label>
                    <select className="aptean-input" value={formWindow} onChange={(e) => setFormWindow(e.target.value as "rolling_30" | "calendar_month")}>
                      <option value="rolling_30">Rolling 30 days</option>
                      <option value="calendar_month">Calendar month</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={creating}>{creating ? "Creating..." : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deletingId && (
        <div className="ac-overlay">
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Confirm Delete</h2>
            </div>
            <div className="ac-dialog__body">
              <div className="ac-alert ac-alert--warning">Are you sure you want to delete this SLO? This cannot be undone.</div>
            </div>
            <div className="ac-dialog__footer">
              <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setDeletingId(null)}>Cancel</button>
              <button type="button" className="aptean-btn aptean-btn--primary" style={{ background: "#a4262c", borderColor: "#a4262c" }} onClick={() => handleDelete(deletingId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
